import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { FocusScope } from '@react-aria/focus';
import { DismissButton } from '@react-aria/overlays';
import cn from 'classnames';

import { DEFAULT_STYLES, SEARCH_DEBOUNCE_MS } from '../../constants';
import {
  readBodyInlineOverflow,
  writeBodyInlineOverflow
} from '../../utils/bodyInlineOverflow';
import { CloseIcon } from '../../icons/CloseIcon';
import { SearchIcon } from '../../icons/SearchIcon';
import { ComboBoxProps, DropdownPlacement } from '../../types';

import { BottomSheet } from '../BottomSheet';
import { FieldErrorMessage } from '../FieldErrorMessage';
import { ShadCNButton } from '../Button';
import { Portal } from '../Portal';
import { CustomLoaderIconContainer } from './styledComponents';

// Custom hook for dropdown positioning
function useDropdownPosition(
  triggerRef: React.RefObject<HTMLElement>,
  overlayRef: React.RefObject<HTMLElement>,
  isOpen: boolean,
  placement: DropdownPlacement = 'bottom',
  offset = 4
) {
  const [position, setPosition] = useState<React.CSSProperties>({});

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !isOpen) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Get overlay dimensions if available
    // Use getBoundingClientRect for actual rendered height (accounts for maxHeight)
    const overlayRect = overlayRef.current?.getBoundingClientRect();
    const overlayHeight =
      overlayRect?.height || overlayRef.current?.offsetHeight || 320;
    const overlayWidth =
      overlayRect?.width ||
      overlayRef.current?.offsetWidth ||
      triggerRect.width;

    let top: number | undefined;
    let bottom: number | undefined;
    let left: number;

    // Calculate base position
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    // Determine vertical placement
    let actualPlacement = placement;
    if (placement === 'auto') {
      actualPlacement =
        spaceBelow >= overlayHeight || spaceBelow >= spaceAbove
          ? 'bottom'
          : 'top';
    } else if (
      placement.startsWith('bottom') &&
      spaceBelow < overlayHeight &&
      spaceAbove > spaceBelow
    ) {
      actualPlacement = placement.replace('bottom', 'top') as DropdownPlacement;
    } else if (
      placement.startsWith('top') &&
      spaceAbove < overlayHeight &&
      spaceBelow > spaceAbove
    ) {
      actualPlacement = placement.replace('top', 'bottom') as DropdownPlacement;
    }

    // Calculate position - use top when appearing below, bottom when appearing above
    if (actualPlacement.startsWith('bottom')) {
      // Bottom: dropdown top edge is offset pixels below trigger bottom edge
      top = triggerRect.bottom + offset;
      bottom = undefined;
    } else if (actualPlacement.startsWith('top')) {
      // Top: use bottom positioning - dropdown bottom edge is offset pixels above trigger top edge
      // bottom is measured from bottom of viewport, so: viewportHeight - (triggerTop - offset)
      top = undefined;
      bottom = viewportHeight - triggerRect.top + offset;
    } else if (
      actualPlacement.startsWith('left') ||
      actualPlacement.startsWith('right')
    ) {
      // Center vertically for left/right placements
      top = triggerRect.top + (triggerRect.height - overlayHeight) / 2;
      bottom = undefined;
    } else {
      top = triggerRect.bottom + offset;
      bottom = undefined;
    }

    // Calculate left position
    if (
      actualPlacement.includes('left') &&
      !actualPlacement.startsWith('left')
    ) {
      left = triggerRect.left;
    } else if (
      actualPlacement.includes('right') &&
      !actualPlacement.startsWith('right')
    ) {
      left = triggerRect.right - overlayWidth;
    } else if (actualPlacement.includes('start')) {
      left = triggerRect.left;
    } else if (actualPlacement.includes('end')) {
      left = triggerRect.right - overlayWidth;
    } else if (actualPlacement.startsWith('left')) {
      left = triggerRect.left - overlayWidth - offset;
    } else if (actualPlacement.startsWith('right')) {
      left = triggerRect.right + offset;
    } else {
      left = triggerRect.left;
    }

    // Ensure dropdown stays within viewport
    if (left < 8) left = 8;
    if (left + overlayWidth > viewportWidth - 8) {
      left = viewportWidth - overlayWidth - 8;
    }

    // Adjust positioning to stay within viewport
    if (top !== undefined) {
      if (top < 8) top = 8;
      if (top + overlayHeight > viewportHeight - 8) {
        top = viewportHeight - overlayHeight - 8;
      }
    }
    if (bottom !== undefined) {
      if (bottom < 8) bottom = 8;
      if (bottom + overlayHeight > viewportHeight - 8) {
        bottom = viewportHeight - overlayHeight - 8;
      }
    }

    const positionStyles: React.CSSProperties = {
      position: 'fixed',
      left: `${left + 2}px`,
      overflow: 'hidden'
    };

    if (top !== undefined) {
      positionStyles.top = `${top}px`;
    }
    if (bottom !== undefined) {
      positionStyles.bottom = `${bottom}px`;
    }

    setPosition(positionStyles);
  }, [triggerRef, overlayRef, isOpen, placement, offset]);

  // Update position when open state changes or on mount
  useLayoutEffect(() => {
    if (isOpen) {
      // Initial position calculation
      updatePosition();
      // Recalculate after dropdown is rendered to get actual height (accounts for maxHeight)
      requestAnimationFrame(() => {
        updatePosition();
      });
    }
  }, [isOpen, updatePosition]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    const handleUpdate = () => updatePosition();

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, updatePosition]);

  return position;
}

export const ComboBox: React.FC<ComboBoxProps> = (props) => {
  const {
    name,
    label,
    value,
    placeholder = 'Search...',
    options,
    onSearch,
    isLoading = false,
    onChange,
    onBlur,
    onInputChange,
    error,
    helperText,
    isDisabled = false,
    isRequired = false,
    renderOption,
    getOptionLabel = (opt) => opt.label,
    getOptionValue = (opt) => opt.value,
    loadingText = 'Loading...',
    emptyStateText = "We couldn't find any matches.",
    customLoader,
    loadingMessage,
    className,
    inputClassName,
    labelClassName,
    optionClassName,
    showButton = true,
    // buttonLabel and buttonVariant props exist but dynamic label is used
    buttonSize = 'md',
    onButtonClick,
    buttonClassName,
    // Dropdown props
    placement = 'bottom',
    offset = 4,
    maxHeight = 344,
    closeOnScroll = true,
    shouldCloseOnBlur = true,
    defaultOpen = false,
    onOpenChange,
    enableAnimation = true,
    // Mobile bottom sheet
    // Set to 0 to always show dropdown (never show bottom sheet)
    mobileBreakpoint = 0,
    // Async loading support
    loadingState,
    // Filtering
    filterFn,
    caseSensitive = false,
    enableClientFilter = false
  } = props;

  const inputId = useId();

  // State
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isInputFocused, setIsInputFocused] = useState(false);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= mobileBreakpoint;
    }
    return false;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= mobileBreakpoint);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileBreakpoint]);

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const listBoxRef = useRef<HTMLUListElement>(null);
  const mobileListRef = useRef<HTMLDivElement>(null); // Ref for mobile bottom sheet scroll container
  const debounceRef = useRef<NodeJS.Timeout>();
  const isClosingRef = useRef(false); // Track if we're in the process of closing
  const isOpeningRef = useRef(false); // Track if we're in the process of opening
  const scrollGracePeriodRef = useRef<number | null>(null); // Track when dropdown opened to ignore auto-scroll
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const justSelectedRef = useRef<string | null>(null); // Track just-selected option label
  const enableClientFilterRef = useRef(enableClientFilter); // Track enableClientFilter to avoid stale closures
  const isTypingRef = useRef(false); // Track if user is actively typing to preserve input value
  // Update ref when enableClientFilter changes
  useEffect(() => {
    enableClientFilterRef.current = enableClientFilter;
  }, [enableClientFilter]);

  // Mobile device detection helper (uses user agent, not just window width)
  // This is needed because mobileBreakpoint=0 means isMobile is always false
  const isActualMobileDevice = useCallback(() => {
    if (typeof navigator === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }, []);

  // Get selected option
  const selectedOption = useMemo(
    () => options.find((opt) => getOptionValue(opt) === value),
    [options, value, getOptionValue]
  );

  // Sync input value with selected option
  useEffect(() => {
    // Don't sync if we're closing to prevent state conflicts
    if (isClosingRef.current) return;

    if (selectedOption && !isOpen) {
      setInputValue(getOptionLabel(selectedOption));
    } else if (!value && !isOpen) {
      setInputValue('');
    }
  }, [value, selectedOption, getOptionLabel, isOpen]);

  // Filter options based on input
  const filteredOptions = useMemo(() => {
    if (!enableClientFilter || !inputValue.trim()) {
      return options;
    }

    // Input still equals the committed selection (e.g. user reopened the list): show all
    // options so they can change selection without clearing, while keeping the label visible.
    if (selectedOption) {
      const selectedLabel = getOptionLabel(selectedOption).trim();
      const trimmedInput = inputValue.trim();
      const inputCmp = caseSensitive ? trimmedInput : trimmedInput.toLowerCase();
      const labelCmp = caseSensitive ? selectedLabel : selectedLabel.toLowerCase();
      if (inputCmp === labelCmp) {
        return options;
      }
    }

    return options.filter((option) => {
      if (filterFn) {
        return filterFn(option, inputValue);
      }

      const labelText = getOptionLabel(option);
      const query = caseSensitive ? inputValue : inputValue.toLowerCase();
      const text = caseSensitive ? labelText : labelText.toLowerCase();

      return text.includes(query);
    });
  }, [
    options,
    inputValue,
    enableClientFilter,
    filterFn,
    caseSensitive,
    getOptionLabel,
    selectedOption
  ]);

  // Handle open/close
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (isDisabled) return;

      // Prevent reopening if we're in the process of closing
      if (open && isClosingRef.current) {
        return;
      }

      // Prevent closing if we're in the process of opening
      // This is critical to prevent dropdown from closing immediately after opening
      if (!open && isOpeningRef.current) {
        return;
      }

      if (!open) {
        // Mark that we're closing
        isClosingRef.current = true;

        // Clear any pending debounced searches
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
          debounceRef.current = undefined;
        }

        setHighlightedIndex(-1);
        setIsOpen(false);
        onOpenChange?.(false);

        // Clear scroll grace period when closing
        scrollGracePeriodRef.current = null;

        // Restore input value to selected option on close
        // Use justSelectedRef if a selection was just made (to avoid race condition with value prop)
        if (justSelectedRef.current) {
          setInputValue(justSelectedRef.current);
          justSelectedRef.current = null;
        } else if (selectedOption) {
          setInputValue(getOptionLabel(selectedOption));
        } else if (!value) {
          // Only clear if there's truly no value
          setInputValue('');
        }

        // Only call onBlur if we're actually closing (not during opening phase)
        // This prevents dropdown from closing immediately after opening
        if (shouldCloseOnBlur && !isOpeningRef.current) {
          onBlur?.();
        }

        // Blur the input to prevent editing the selected value after dropdown closes
        setTimeout(() => {
          inputRef.current?.blur();
          mobileInputRef.current?.blur();
          setIsInputFocused(false);
        }, 0);

        // Reset closing flag after a brief delay to allow state to settle
        // Use requestAnimationFrame to ensure this happens after React's state update
        requestAnimationFrame(() => {
          setTimeout(() => {
            isClosingRef.current = false;
          }, 100);
        });
      } else {
        // Opening: ensure closing flag is cleared and set opening flag
        isClosingRef.current = false;
        isOpeningRef.current = true;

        // Restore selected label when opening so the input is not blank while the list
        // highlights the current value. For client-side filtering, filteredOptions returns
        // all options while input exactly matches the selected label (see useMemo above).
        if (enableClientFilter) {
          isTypingRef.current = false;
          if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = undefined;
          }
          if (selectedOption) {
            setInputValue(getOptionLabel(selectedOption));
          } else {
            setInputValue('');
          }
        } else if (selectedOption) {
          setInputValue(getOptionLabel(selectedOption));
        } else if (!isTypingRef.current) {
          // Async comboboxes: preserve draft text from controlled `value` when it
          // does not resolve to an option (e.g. partial search with label-as-value).
          if (value !== undefined && value !== null && value !== '') {
            setInputValue(String(value));
          } else {
            setInputValue('');
          }
        }

        setIsOpen(true);
        onOpenChange?.(true);

        // Set scroll grace period timestamp to ignore browser auto-scroll
        // This prevents dropdown from closing when browser scrolls to bring input into view
        scrollGracePeriodRef.current = Date.now();

        // Reset opening flag after dropdown is fully opened
        // Use requestAnimationFrame to ensure DOM is updated
        // Increased delay to prevent premature closing
        requestAnimationFrame(() => {
          setTimeout(() => {
            // On desktop, ensure input maintains focus after dropdown opens
            // This prevents dropdown from closing due to blur
            if (
              !isMobile &&
              inputRef.current &&
              document.activeElement !== inputRef.current
            ) {
              inputRef.current.focus();
            }
            // Reset opening flag after focus is established
            // Use a longer delay to ensure dropdown is fully rendered and stable
            // Extended to 500ms total to account for browser auto-scroll timing on mobile
            setTimeout(() => {
              isOpeningRef.current = false;
            }, 350);
          }, 150);
        });
      }
    },
    [
      isDisabled,
      onOpenChange,
      shouldCloseOnBlur,
      onBlur,
      selectedOption,
      getOptionLabel,
      enableClientFilter,
      value
    ]
  );

  // Handle selection
  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange?.(optionValue);
      const selectedOpt = options.find(
        (opt) => getOptionValue(opt) === optionValue
      );
      if (selectedOpt) {
        const label = getOptionLabel(selectedOpt);
        setInputValue(label);
        // Store the selected label so handleOpenChange doesn't clear it
        justSelectedRef.current = label;
      }
      handleOpenChange(false);
    },
    [onChange, options, getOptionValue, getOptionLabel, handleOpenChange]
  );

  // Handle input change
  const handleInputValueChange = useCallback(
    (val: string) => {
      // Don't process input changes if we're closing
      // if (isClosingRef.current) return;

      // Mark that user is actively typing
      isTypingRef.current = true;
      setInputValue(val);
      onInputChange?.(val);

      // Open dropdown when typing (desktop only - mobile uses bottom sheet input)
      // Also open if loading starts (to show loading indicator)
      if (!isOpen && val.trim() && !isMobile) {
        handleOpenChange(true);
      }

      // Debounced search
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        // Don't fire search if we're closing
        if (!isClosingRef.current) {
          onSearch?.(val);
        }
        // Reset typing flag after search completes
        isTypingRef.current = false;
      }, SEARCH_DEBOUNCE_MS);
    },
    [isOpen, isMobile, handleOpenChange, onInputChange, onSearch]
  );

  // Clean up debounce on unmount and when closing
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = undefined;
      }
    };
  }, []);

  // Clean up debounce when closing
  useEffect(() => {
    if (!isOpen && debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
    }
  }, [isOpen]);

  // Focus mobile input when bottom sheet opens
  // This is critical for mobile keyboard to appear and stay open
  useEffect(() => {
    if (isOpen && isMobile && mobileInputRef.current) {
      // Set opening flag to prevent blur handlers from interfering
      isOpeningRef.current = true;

      // Use multiple attempts with increasing delays to ensure focus is maintained
      // This accounts for BottomSheet's autoFocus and animation timing
      const focusAttempts = [
        () => {
          // First attempt: after BottomSheet animation starts
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (mobileInputRef.current && isOpen) {
                mobileInputRef.current.focus();
              }
            }, 200);
          });
        },
        () => {
          // Second attempt: after BottomSheet is fully rendered
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (
                mobileInputRef.current &&
                isOpen &&
                document.activeElement !== mobileInputRef.current
              ) {
                mobileInputRef.current.focus();
              }
            }, 350);
          });
        },
        () => {
          // Third attempt: final check to ensure focus is maintained
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (
                mobileInputRef.current &&
                isOpen &&
                document.activeElement !== mobileInputRef.current
              ) {
                mobileInputRef.current.focus();
              }
              // Reset opening flag after all attempts
              isOpeningRef.current = false;
            }, 500);
          });
        }
      ];

      // Execute all focus attempts
      focusAttempts.forEach((attempt) => attempt());
    } else if (!isOpen && isMobile) {
      // Reset opening flag when bottom sheet closes
      isOpeningRef.current = false;
    }
  }, [isOpen, isMobile]);

  // Maintain input focus when dropdown opens on mobile (for keyboard to stay open)
  // This is critical for mobile devices where keyboard should remain open
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Detect if we're on a mobile device (even if using dropdown instead of bottom sheet)
      const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      if (isMobileDevice) {
        // Use requestAnimationFrame to ensure DOM is ready, then small delay for rendering
        requestAnimationFrame(() => {
          setTimeout(() => {
            // Only refocus if:
            // 1. Input ref still exists
            // 2. Dropdown is still open
            // 3. Input is not already focused
            // 4. We're in the opening phase (to prevent conflicts)
            if (
              inputRef.current &&
              isOpen &&
              document.activeElement !== inputRef.current &&
              isOpeningRef.current
            ) {
              inputRef.current.focus();
            }
          }, 100);
        });
      }
    }
  }, [isOpen]);

  // Auto-scroll to selected option when dropdown/bottom sheet opens
  useEffect(() => {
    if (!isOpen || !value) return;

    // Use setTimeout to ensure options are fully rendered
    // requestAnimationFrame ensures DOM is ready, then small delay for rendering
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Desktop dropdown: scroll in listBoxRef
        if (!isMobile && listBoxRef.current) {
          const selectedItem = listBoxRef.current.querySelector(
            `[role="option"][aria-selected="true"]`
          ) as HTMLElement;
          if (selectedItem) {
            selectedItem.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth'
            });
          }
        }

        // Mobile bottom sheet: scroll in mobileListRef
        if (isMobile && mobileListRef.current) {
          const selectedItem = mobileListRef.current.querySelector(
            `[role="option"][aria-selected="true"]`
          ) as HTMLElement;
          if (selectedItem) {
            selectedItem.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth'
            });
          }
        }
      }, 50); // Small delay to ensure options are rendered
    });
  }, [isOpen, value, isMobile, filteredOptions]);

  // Custom positioning hook - use container ref for positioning
  const positionStyles = useDropdownPosition(
    containerRef,
    popoverRef,
    isOpen && !isMobile,
    placement,
    offset
  );

  // Lock body scroll when desktop dropdown is open (inline style only; CSS rules are unchanged)
  useEffect(() => {
    if (!(isOpen && !isMobile)) return;

    const previousInlineOverflow = readBodyInlineOverflow();
    document.body.style.setProperty('overflow', 'hidden');

    return () => {
      writeBodyInlineOverflow(previousInlineOverflow);
    };
  }, [isOpen, isMobile]);

  // Close on scroll (desktop only, or mobile with grace period protection)
  useEffect(() => {
    if (!isOpen || !closeOnScroll) return;

    const handleScroll = (event: Event) => {
      // Don't close if we're in the opening phase
      if (isOpeningRef.current) {
        return;
      }

      // Check if we're within grace period (browser auto-scroll protection)
      // This prevents dropdown from closing when browser auto-scrolls on mobile
      if (scrollGracePeriodRef.current !== null) {
        const gracePeriodMs = 700; // 700ms grace period for browser auto-scroll
        const timeSinceOpen = Date.now() - scrollGracePeriodRef.current;

        if (timeSinceOpen < gracePeriodMs) {
          // Within grace period - check if it's a mobile device
          // On mobile, ignore scroll during grace period (likely browser auto-scroll)
          if (isActualMobileDevice()) {
            return;
          }
          // On desktop, still respect grace period but be more lenient
          // Only ignore if very recent (first 300ms)
          if (timeSinceOpen < 300) {
            return;
          }
        } else {
          // Grace period expired, clear the ref
          scrollGracePeriodRef.current = null;
        }
      }

      // Don't close if scrolling inside the dropdown
      const target = event.target as Node;
      if (popoverRef.current?.contains(target)) return;

      // Close dropdown on scroll outside
      handleOpenChange(false);
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen, closeOnScroll, handleOpenChange, isActualMobileDevice]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if we're in the opening phase
      if (isOpeningRef.current) {
        return;
      }

      const target = event.target as Node;
      if (
        containerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      handleOpenChange(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isMobile, handleOpenChange]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (isDisabled || isClosingRef.current) return;

      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          if (isOpen && highlightedIndex >= 0) {
            const option = filteredOptions[highlightedIndex];
            if (option && !option.disabled) {
              handleSelect(getOptionValue(option));
            }
          } else if (!isOpen && !isMobile) {
            // Don't open on Enter for mobile (mobile uses bottom sheet)
            handleOpenChange(true);
          }
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen && !isMobile) {
            // Don't open on ArrowDown for mobile
            handleOpenChange(true);
          } else if (isOpen) {
            setHighlightedIndex((prev) => {
              const next = prev + 1;
              return next >= filteredOptions.length ? 0 : next;
            });
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen && !isMobile) {
            // Don't open on ArrowUp for mobile
            handleOpenChange(true);
          } else if (isOpen) {
            setHighlightedIndex((prev) => {
              const next = prev - 1;
              return next < 0 ? filteredOptions.length - 1 : next;
            });
          }
          break;
        case 'Escape':
          event.preventDefault();
          if (isOpen) {
            handleOpenChange(false);
            // Only focus input on desktop
            if (!isMobile) {
              inputRef.current?.focus();
            }
          }
          break;
        case 'Tab':
          if (isOpen) {
            handleOpenChange(false);
          }
          break;
      }
    },
    [
      isDisabled,
      isOpen,
      isMobile,
      highlightedIndex,
      filteredOptions,
      handleSelect,
      handleOpenChange,
      getOptionValue
    ]
  );

  // Handle clear
  const handleClear = useCallback(() => {
    setInputValue('');
    onChange?.('');
    onInputChange?.('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    onSearch?.('');
  }, [onChange, onSearch, onInputChange]);

  const handleOnButtonClick = () => {
    // Use handleOpenChange to properly close with isClosingRef protection
    handleOpenChange(false);
    // Blur the input to prevent dropdown from reopening immediately
    // Use setTimeout to ensure the blur happens after the click event completes
    setTimeout(() => {
      inputRef.current?.blur();
      mobileInputRef.current?.blur();
      setIsInputFocused(false);
    }, 0);
    onButtonClick?.();
  };

  // Determine loading state
  const isCurrentlyLoading = useMemo(() => {
    if (loadingState) {
      return (
        loadingState === 'loading' ||
        loadingState === 'filtering' ||
        loadingState === 'loadingMore'
      );
    }
    return isLoading;
  }, [loadingState, isLoading]);

  const hasError = Boolean(error);
  // Hide error when input is focused or dropdown is open (user is actively interacting)
  const showError = hasError && !isInputFocused && !isOpen;
  // Show helper text when no error, or when error is hidden (input focused or dropdown open)
  const showHelperText = (!hasError || isInputFocused || isOpen) && helperText;
  const showClearButton = inputValue && !isDisabled;

  // Show sticky button based on local input value (when feature is enabled via showButton prop)
  const shouldShowStickyButton = showButton && inputValue.trim().length > 0;

  // Animation classes
  const animationClasses = enableAnimation
    ? cn(
      'transition-all duration-200 ease-out',
      isOpen
        ? 'opacity-100 transform scale-100'
        : 'opacity-0 transform scale-95'
    )
    : '';

  // Render mobile content
  const renderMobileContent = () => (
    <>
      {/* Search input for mobile */}
      <div className="px-4 py-1 flex-shrink-0">
        <div
          className={cn(
            DEFAULT_STYLES.field__inputContainer,
            hasError
              ? 'border-errorRed'
              : isOpen || isInputFocused
                ? 'border-lightIndigo600 ring-2 ring-lightIndigo600 ring-opacity-50 input-container-focused'
                : DEFAULT_STYLES.field__inputContainerDefault,
            isDisabled && DEFAULT_STYLES.field__inputContainerDisabled
          )}
        >
          <input
            ref={mobileInputRef}
            type="text"
            value={inputValue}
            placeholder={placeholder}
            onChange={(e) => {
              // Don't process changes if we're closing
              if (isClosingRef.current) return;

              const newValue = e.target.value;
              // Mark that user is actively typing
              isTypingRef.current = true;
              setInputValue(newValue);
              onInputChange?.(newValue);

              // Debounced search for mobile input
              if (debounceRef.current) {
                clearTimeout(debounceRef.current);
              }
              debounceRef.current = setTimeout(() => {
                if (!isClosingRef.current) {
                  onSearch?.(newValue);
                }
                // Reset typing flag after search completes
                isTypingRef.current = false;
              }, SEARCH_DEBOUNCE_MS);
            }}
            onFocus={(e) => {
              // Track focus state for error visibility
              setIsInputFocused(true);
              // Prevent focus from causing issues during close
              if (isClosingRef.current) {
                e.target.blur();
                return;
              }
              // Ensure opening flag is set when mobile input gets focus
              // This helps maintain keyboard on mobile devices
              if (isMobile && isOpen) {
                isOpeningRef.current = true;
                // Reset after a short delay
                setTimeout(() => {
                  isOpeningRef.current = false;
                }, 200);
              }
            }}
            onBlur={() => {
              // Don't process blur if we're opening the bottom sheet/dropdown
              // This prevents keyboard from closing on mobile when bottom sheet opens
              if (isOpeningRef.current && isOpen) {
                // Keep input focused to maintain keyboard on mobile
                // Use multiple attempts to ensure focus is maintained
                requestAnimationFrame(() => {
                  setTimeout(() => {
                    if (mobileInputRef.current && isOpen) {
                      // On mobile, maintain focus if bottom sheet is open
                      // This is critical for keyboard to stay open
                      mobileInputRef.current.focus();
                      // Double-check after another frame
                      requestAnimationFrame(() => {
                        if (
                          mobileInputRef.current &&
                          isOpen &&
                          document.activeElement !== mobileInputRef.current
                        ) {
                          mobileInputRef.current.focus();
                        }
                      });
                    }
                  }, 50);
                });
                return;
              }
              // Track blur state for error visibility
              // Use setTimeout to allow click events to process first
              setTimeout(() => {
                // Only update focus state if we're not opening and bottom sheet is closed
                if (!isOpeningRef.current && !isOpen) {
                  setIsInputFocused(false);
                }
              }, 200);
            }}
            disabled={isDisabled}
            className={cn(
              DEFAULT_STYLES.field__input,
              DEFAULT_STYLES.field__inputText,
              DEFAULT_STYLES.field__inputPlaceholder,
              isDisabled && DEFAULT_STYLES.field__inputDisabled
            )}
          />

          {/* Clear button */}
          {showClearButton && (
            <button
              type="button"
              onClick={handleClear}
              className="flex-shrink-0 mb-auto flex mt-auto text-cement hover:text-slateText transition-colors"
              tabIndex={-1}
              aria-label="Clear selection"
            >
              <CloseIcon className="w-4 h-4 mb-auto flex mt-auto" />
            </button>
          )}
        </div>
      </div>

      {/* Options List */}
      <div
        ref={mobileListRef}
        className="overflow-y-auto flex-1 p-2 px-3"
        style={{
          maxHeight: shouldShowStickyButton
            ? 'calc(80vh - 200px)'
            : 'calc(80vh - 120px)',
          minHeight: '30vh'
        }}
      >
        {isCurrentlyLoading && loadingState !== 'loadingMore' ? (
          <div className="flex flex-col items-center gap-2 py-4">
            {customLoader && (
              <CustomLoaderIconContainer>
                {customLoader}
              </CustomLoaderIconContainer>
            )}
            <div className={DEFAULT_STYLES.dropdown__loading}>
              {loadingMessage || loadingText}
            </div>
          </div>
        ) : filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <div
              key={getOptionValue(option)}
              onClick={() => {
                if (!option.disabled) {
                  handleSelect(getOptionValue(option));
                }
              }}
              role="option"
              aria-selected={getOptionValue(option) === value}
              aria-disabled={option.disabled}
              className={cn(
                DEFAULT_STYLES.combobox__bottomSheet__option,
                getOptionValue(option) === value &&
                DEFAULT_STYLES.combobox__bottomSheet__optionSelected,
                option.disabled &&
                DEFAULT_STYLES.combobox__bottomSheet__optionDisabled,
                optionClassName
              )}
            >
              <div className="flex items-center justify-between w-full">
                <span className="flex-1 break-words whitespace-normal">
                  {renderOption ? renderOption(option) : getOptionLabel(option)}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className={DEFAULT_STYLES.dropdown__noResults}>
            {loadingState === 'error' ? (
              'Error loading options'
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 text-base">
                <SearchIcon width={36} height={36} className="mb-2" />
                <p className="text-center font-semibold text-black">Oops... </p>
                <p className="text-center text-black text-sm">{emptyStateText}</p>
                <p className="text-center text-black text-sm">
                  Click below to Add New ⬇️
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fixed button at bottom */}
      {shouldShowStickyButton && (
        <div className="p-3 flex-shrink-0 bg-white">
          <ShadCNButton
            type="button"
            variant="outline"
            size={buttonSize}
            onClick={handleOnButtonClick}
            fullWidth
            className={cn(
              DEFAULT_STYLES.combobox__bottomSheet__button,
              buttonClassName
            )}
            leftElement={
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 2.33333V11.6667M2.33333 7H11.6667"
                  stroke="#4F46E5"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
          >
            {`Add New "${inputValue}"`}
          </ShadCNButton>
        </div>
      )}
    </>
  );
  // Render desktop options
  const renderDesktopOptions = () => {
    return (
      <ul
        ref={listBoxRef}
        role="listbox"
        aria-labelledby={label ? `${inputId}-label` : undefined}
        className="py-2 px-2 overflow-y-auto outline-none flex-1"
        style={{
          maxHeight: shouldShowStickyButton
            ? `${maxHeight - 80}px`
            : `${maxHeight}px`
        }}
      >
        {filteredOptions.length > 0
          ? filteredOptions.map((option, index) => (
            <li
              key={getOptionValue(option)}
              role="option"
              aria-selected={getOptionValue(option) === value}
              aria-disabled={option.disabled}
              onClick={() => {
                if (!option.disabled) {
                  handleSelect(getOptionValue(option));
                }
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={cn(
                DEFAULT_STYLES.dropdown__option,
                getOptionValue(option) === value &&
                DEFAULT_STYLES.dropdown__optionSelected,
                option.disabled && DEFAULT_STYLES.dropdown__optionDisabled,
                highlightedIndex === index &&
                !option.disabled &&
                'bg-bottomSheetOptionBg',
                optionClassName
              )}
            >
              <div className="flex items-center justify-between w-full">
                <span className="flex-1 break-words whitespace-normal">
                  {renderOption
                    ? renderOption(option)
                    : getOptionLabel(option)}
                </span>
              </div>
            </li>
          ))
          : (
            <li className={DEFAULT_STYLES.dropdown__noResults}>
              <div className="flex flex-col items-center justify-center gap-2 text-base">
                <SearchIcon width={36} height={36} className="mb-2" />
                <p className="text-center font-semibold text-black">Oops... </p>
                <p className="text-center text-black text-sm">{emptyStateText}</p>
                <p className="text-center text-black text-sm">
                  Click below to Add New ⬇️
                </p>
              </div>
            </li>
          )}
      </ul>
    );
  };

  return (
    <div className={cn(DEFAULT_STYLES.field, className)}>
      {label && (
        <label
          id={`${inputId}-label`}
          htmlFor={inputId}
          className={cn(
            DEFAULT_STYLES.field__label,
            isDisabled && DEFAULT_STYLES.field__labelDisabled,
            labelClassName,
            'informedInputLabelText'
          )}
        >
          {label}
          {isRequired && (
            <span className={DEFAULT_STYLES.field__labelRequired}>*</span>
          )}
        </label>
      )}

      <input type="hidden" name={name} value={value || ''} />

      <div className="relative">
        {/* Input with flexbox layout for icons */}
        <div
          ref={containerRef}
          onClick={() => {
            // In mobile view, clicking the container should open the bottom sheet
            if (isMobile && !isDisabled && !isOpen) {
              isOpeningRef.current = true;
              handleOpenChange(true);
            }
          }}
          className={cn(
            DEFAULT_STYLES.field__inputContainer,
            hasError
              ? 'border-errorRed ring-errorRed ring-opacity-50'
              : isOpen || isInputFocused
                ? 'border-lightIndigo600 ring-2 ring-lightIndigo600 ring-opacity-50 input-container-focused'
                : DEFAULT_STYLES.field__inputContainerDefault,
            isDisabled && DEFAULT_STYLES.field__inputContainerDisabled,
            isMobile && !isDisabled && 'cursor-pointer'
          )}
        >
          {/* Search icon (decorative, left side) */}
          <div
            className="flex items-center justify-center flex-shrink-0 text-cement pointer-events-none"
            aria-hidden="true"
          >
            <SearchIcon width={16} height={16} />
          </div>

          <input
            ref={inputRef}
            id={inputId}
            type="text"
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            aria-controls={isOpen ? `${inputId}-listbox` : undefined}
            aria-labelledby={label ? `${inputId}-label` : undefined}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => handleInputValueChange(e.target.value)}
            style={isDisabled ? { WebkitTextFillColor: '#94a3b8' } : undefined}
            onFocus={() => {
              // Track focus state for error visibility
              setIsInputFocused(true);
              // In mobile view, open bottom sheet when input is focused
              if (isMobile && !isDisabled && !isOpen) {
                // Set opening flag before opening to prevent blur handler from interfering
                // This is critical for mobile keyboard to stay open
                isOpeningRef.current = true;
                handleOpenChange(true);
                return;
              }
              // Don't open on focus if we're closing
              if (isDisabled || isClosingRef.current) return;
              // Open dropdown even with zero options (async empty / loading UX)
              isOpeningRef.current = true;
              handleOpenChange(true);
            }}
            onClick={() => {
              // In mobile view, clicking the input should open the bottom sheet
              if (isMobile && !isDisabled && !isOpen) {
                isOpeningRef.current = true;
                handleOpenChange(true);
              }
            }}
            onBlur={() => {
              // Don't process blur if we're opening the dropdown
              // This prevents dropdown from closing immediately after opening
              if (isOpeningRef.current && isOpen) {
                // Keep input focused to maintain dropdown open state
                requestAnimationFrame(() => {
                  if (inputRef.current && isOpen && !isMobile) {
                    // On desktop, refocus input if dropdown is still open
                    if (document.activeElement !== inputRef.current) {
                      inputRef.current.focus();
                    }
                  }
                });
                return;
              }
              // Track blur state for error visibility
              // Use setTimeout to allow click events to process first
              setTimeout(() => {
                // Only update focus state if we're not opening and dropdown is closed
                if (!isOpeningRef.current && !isOpen) {
                  setIsInputFocused(false);
                }
              }, 200);
            }}
            onKeyDown={handleKeyDown}
            className={cn(
              DEFAULT_STYLES.field__input,
              isDisabled
                ? DEFAULT_STYLES.field__inputDisabled
                : DEFAULT_STYLES.field__inputText,
              DEFAULT_STYLES.field__inputPlaceholder,
              isMobile && 'cursor-pointer',
              inputClassName
            )}
          />

          {/* Clear button */}
          {showClearButton && (
            <button
              type="button"
              onClick={handleClear}
              className="flex-shrink-0 mb-auto flex mt-auto text-cement hover:text-slateText transition-colors"
              tabIndex={-1}
              aria-label="Clear selection"
            >
              <CloseIcon className="w-4 h-4 mb-auto flex mt-auto" />
            </button>
          )}
        </div>

        {/* Desktop Dropdown - Rendered via Portal */}
        {isOpen && !isMobile && (
          <Portal>
            <FocusScope contain restoreFocus autoFocus={false}>
              <div
                ref={popoverRef}
                id={`${inputId}-listbox`}
                className={cn(
                  'bg-white border border-lightBlueGray300 rounded-lg shadow-lg flex flex-col',
                  animationClasses,
                  'base__dropdown'
                )}
                style={{
                  ...positionStyles,
                  width: containerRef.current?.offsetWidth,
                  maxHeight: shouldShowStickyButton
                    ? `${maxHeight}px`
                    : `${maxHeight + 80}px`,
                  zIndex: 9999
                }}
              >
                <DismissButton onDismiss={() => handleOpenChange(false)} />

                {isCurrentlyLoading && loadingState !== 'loadingMore' ? (
                  <div className="flex flex-col items-center gap-2 py-8">
                    {customLoader && (
                      <CustomLoaderIconContainer>
                        {customLoader}
                      </CustomLoaderIconContainer>
                    )}
                    <div className={DEFAULT_STYLES.dropdown__loading}>
                      {loadingMessage || loadingText}
                    </div>
                  </div>
                ) : (
                  renderDesktopOptions()
                )}

                {/* Load more indicator */}
                {loadingState === 'loadingMore' && (
                  <div className="p-2 text-center text-sm text-cement border-t border-lightBlueGray300">
                    <svg
                      className="w-4 h-4 mx-auto animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="#475569"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span className="mt-1 block">Loading more...</span>
                  </div>
                )}

                {/* Fixed button at bottom */}
                {shouldShowStickyButton && (
                  <div className="p-4 flex-shrink-0 bg-white">
                    <ShadCNButton
                      type="button"
                      variant="outline"
                      size={buttonSize}
                      onClick={handleOnButtonClick}
                      fullWidth
                      className={cn(
                        DEFAULT_STYLES.combobox__dropdown__button,
                        buttonClassName
                      )}
                      leftElement={
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7 2.33333V11.6667M2.33333 7H11.6667"
                            stroke="#4F46E5"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      }
                    >
                      {`Add New "${inputValue}"`}
                    </ShadCNButton>
                  </div>
                )}

                <DismissButton onDismiss={() => handleOpenChange(false)} />
              </div>
            </FocusScope>
          </Portal>
        )}

        {/* Mobile Bottom Sheet */}
        {isOpen && isMobile && (
          <BottomSheet
            isOpen={isOpen}
            onClose={() => {
              // Ensure we close deterministically
              if (!isClosingRef.current) {
                handleOpenChange(false);
              }
            }}
            title={label}
            showCloseButton={true}
            className={DEFAULT_STYLES.combobox__bottomSheet}
          >
            {renderMobileContent()}
          </BottomSheet>
        )}
      </div>

      {/* Single feedback container - always rendered to prevent layout shift */}
      {(hasError || showHelperText) && (
        <p
          id={`${inputId}-error`}
          className={cn(
            DEFAULT_STYLES.field__message,
            showError
              ? 'text-errorTextColor'
              : showHelperText
                ? 'text-helperTextColor'
                : 'invisible h-4'
          )}
          role={showError ? 'alert' : undefined}
        >
          {showError && error ? (
            <FieldErrorMessage message={error} />
          ) : showHelperText ? (
            helperText
          ) : (
            ''
          )}
        </p>
      )}
    </div>
  );
};

ComboBox.displayName = 'ComboBox';
